import React, { useEffect, useRef, useState } from 'react';

export interface IconDropdownMenuItem {
    id: string;
    label: string;
}

interface IconDropdownMenuProps {
    items: IconDropdownMenuItem[];
    onSelect: (id: string) => void;
    buttonLabel?: string;
    triggerContent?: React.ReactNode;
    menuTitle?: string;
    emptyText?: string;
    disabled?: boolean;
    direction?: 'up' | 'down';
    className?: string;
    buttonClassName?: string;
    showButtonBorder?: boolean;
    iconOnly?: boolean;
}

export const IconDropdownMenu: React.FC<IconDropdownMenuProps> = ({
    items,
    onSelect,
    buttonLabel = '+',
    triggerContent,
    menuTitle = 'Select Option',
    emptyText = 'No options available',
    disabled = false,
    direction = 'up',
    className = '',
    buttonClassName = '',
    showButtonBorder = true,
    iconOnly = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const isDisabled = disabled || items.length === 0;
    const menuPositionClass = direction === 'up' ? 'right-0 bottom-10' : 'right-0 top-10';
    const borderClass = showButtonBorder ? 'border border-accent-electric/35 hover:border-accent-electric/55' : 'border border-transparent hover:border-transparent';
    const chromeClass = iconOnly
        ? 'bg-transparent shadow-none hover:shadow-none'
        : 'bg-gradient-to-br from-accent-electric/20 to-accent-electric/5 shadow-[0_0_12px_rgb(var(--accent-electric-rgb)/0.18)] hover:shadow-[0_0_18px_rgb(var(--accent-electric-rgb)/0.26)]';
    const focusClass = iconOnly ? 'focus:ring-0' : 'focus:ring-2 focus:ring-accent-electric/40';

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            <button
                type="button"
                onClick={() => setIsOpen(prev => !prev)}
                disabled={isDisabled}
                className={`h-8 w-8 rounded-lg ${borderClass} ${chromeClass} text-accent-electric text-lg font-black leading-none text-center cursor-pointer transition-all focus:outline-none ${focusClass} disabled:opacity-40 disabled:cursor-not-allowed ${buttonClassName}`}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                title={menuTitle}
            >
                {triggerContent ?? buttonLabel}
            </button>

            {isOpen && (
                <div className={`absolute z-30 w-60 rounded-xl border border-accent-electric/30 bg-bg-secondary/95 backdrop-blur-md shadow-[0_0_24px_rgb(var(--accent-electric-rgb)/0.18)] overflow-hidden ${menuPositionClass}`}>
                    <div className="px-3 py-2 text-micro font-bold uppercase tracking-wider text-text-muted border-b border-divider-subtle">
                        {menuTitle}
                    </div>
                    <div className="max-h-56 overflow-y-auto custom-scrollbar py-1">
                        {items.length > 0 ? (
                            items.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => {
                                        onSelect(item.id);
                                        setIsOpen(false);
                                    }}
                                    className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-accent-electric/12 transition-colors"
                                >
                                    {item.label}
                                </button>
                            ))
                        ) : (
                            <div className="px-3 py-2 text-xs text-text-muted">{emptyText}</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
